import { useRef, useState, useEffect, useCallback } from "react";
import TrafficLights from "./TrafficLights";

export default function Window({ title, onClose, zIndex, onFocus, children, initialSize, initialPos }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [pos, setPos] = useState(initialPos || { x: 60, y: 60 });
  const [size] = useState(initialSize || { w: 560, h: 460 });
  const [mounted, setMounted] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (isMobile) return;
      onFocus();
      const startX = e.clientX - pos.x;
      const startY = e.clientY - pos.y;
      dragRef.current = { startX, startY };
      const move = (ev) => {
        if (!dragRef.current) return;
        setPos({
          x: Math.max(0, Math.min(ev.clientX - dragRef.current.startX, window.innerWidth - 120)),
          y: Math.max(28, Math.min(ev.clientY - dragRef.current.startY, window.innerHeight - 60)),
        });
      };
      const up = () => {
        dragRef.current = null;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [isMobile, onFocus, pos]
  );

  const style = isMobile
    ? { position: "fixed", inset: 0, zIndex, paddingTop: "env(safe-area-inset-top)" }
    : {
        position: "absolute",
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        zIndex,
        transform: mounted ? "scale(1)" : "scale(0.92)",
        opacity: mounted ? 1 : 0,
        transition: "transform 180ms cubic-bezier(0.2,1.2,0.4,1), opacity 160ms ease",
      };

  return (
    <div
      data-testid={`window-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex flex-col rounded-xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.55)] border border-black/20 bg-white/95 backdrop-blur-xl"
      style={style}
      onPointerDown={onFocus}
    >
      <div
        className="flex items-center h-11 md:h-10 bg-gradient-to-b from-[#f6f6f6] to-[#e8e8e8] border-b border-black/10 select-none shrink-0"
        onPointerDown={onPointerDown}
        style={{ cursor: isMobile ? "default" : "grab" }}
      >
        <TrafficLights onClose={onClose} />
        <div className="flex-1 text-center text-[13px] font-semibold text-[#3a3a3c] -ml-12 pointer-events-none">{title}</div>
      </div>
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}
